import React, { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';

import Breadcrumbs from '@components/Common/Breadcrumb';
import { TablePagination } from '@components/supply-chain/TablePagination';
import useQuery from '@helpers/JIFI/useQuery';
import { Badge } from '@shadcn/components/ui/badge';
import { Button } from '@shadcn/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@shadcn/components/ui/card';
import { Input } from '@shadcn/components/ui/input';
import Select from '@shadcn/components/ui/react-select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from '@shadcn/components/ui/table';

import {
  fetchStockAllocations,
  normalizeAllocationResponse,
} from './stock-allocation.service';
import { StatusBadge } from '../orders/components/StatusBadge';

const DEFAULT_FILTERS = {
  location: 'all',
  order: '',
  sku: '',
  fromDate: '',
  toDate: '',
};

const PER_PAGE_OPTIONS = [10, 20, 50, 100].map(value => ({
  value,
  label: String(value),
}));

const LOCATION_SELECT_STYLES = {
  control: (provided, state) => ({
    ...provided,
    minHeight: 38,
    height: 38,
    borderColor: state.isFocused ? '#94a3b8' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 1px #94a3b8' : 'none',
    '&:hover': { borderColor: '#94a3b8' },
  }),
  valueContainer: provided => ({ ...provided, padding: '0 12px' }),
  indicatorsContainer: provided => ({ ...provided, height: 38 }),
  indicatorSeparator: provided => ({ ...provided, backgroundColor: '#ced4da' }),
  dropdownIndicator: provided => ({ ...provided, color: '#475569' }),
  menu: provided => ({ ...provided, zIndex: 50 }),
  menuPortal: provided => ({ ...provided, zIndex: 9999 }),
  option: (provided, state) => ({
    ...provided,
    color: '#0f172a',
    backgroundColor: state.isSelected
      ? '#dbeafe'
      : state.isFocused
        ? '#eff6ff'
        : '#fff',
    cursor: 'pointer',
  }),
};

const LocationBadge = ({ location }) => {
  if (!location) return '—';

  const isNone = location.toLowerCase() === 'none';
  return (
    <Badge
      className={isNone
        ? 'border-slate-200 bg-white text-xs font-medium text-slate-600'
        : 'border-emerald-100 bg-emerald-50 text-xs font-semibold text-emerald-500'}
    >
      {location}
    </Badge>
  );
};

const StockAllocationPage = () => {
  const [locations] = useQuery({ url: '/inventory/getLocations' });
  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [summary, setSummary] = useState({ reserved: 0, unallocated: 0 });
  const [state, setState] = useState('loading');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    document.title = 'Stock Allocation | JIFI';
  }, []);

  useEffect(() => {
    let cancelled = false;
    const request = fetchStockAllocations({
      ...filters,
      order: filters.order.trim().replace(/^#/, ''),
      sku: filters.sku.trim(),
      page,
      perPage,
    });

    setState('loading');
    setErrorMessage('');

    request
      .then(response => {
        if (cancelled) return;
        const normalized = normalizeAllocationResponse(response.body);
        setRows(normalized.rows);
        setCount(normalized.count);
        setSummary(normalized.summary);
        setState('success');
      })
      .catch(error => {
        if (cancelled || error?.code === 'ABORTED') return;
        setRows([]);
        setCount(0);
        setSummary({ reserved: 0, unallocated: 0 });
        setState('error');
        setErrorMessage(
          error?.status === 404
            ? 'Stock allocation data is not available yet. The frontend is ready for the inventory allocation endpoint.'
            : 'Unable to load stock allocation data. Please try again.'
        );
      });

    return () => {
      cancelled = true;
      request.abort();
    };
  }, [filters, page, perPage]);

  const applyFilters = event => {
    event?.preventDefault();
    setPage(1);
    setFilters({
      ...draftFilters,
      order: draftFilters.order.trim(),
      sku: draftFilters.sku.trim(),
    });
  };

  const handlePerPageChange = value => {
    setPerPage(value);
    setPage(1);
  };

  const groupedRows = useMemo(() => {
    const groups = new Map();

    rows.forEach(row => {
      const groupKey = row.orderId || row.id;
      if (!groups.has(groupKey)) groups.set(groupKey, []);
      groups.get(groupKey).push(row);
    });

    return Array.from(groups.values()).flatMap(group => group.map((row, index) => ({
      ...row,
      orderGroupIndex: index,
      orderGroupSize: group.length,
    })));
  }, [rows]);

  const locationNamesByCode = useMemo(() => new Map(
    (Array.isArray(locations) ? locations : []).map(location => [
      location.code || location.name,
      location.display_name || location.name || location.code,
    ])
  ), [locations]);

  const locationOptions = useMemo(() => [
    { value: 'all', label: 'All Locations' },
    { value: 'None', label: 'None' },
    ...(Array.isArray(locations) ? locations : []).map(location => ({
      value: location.code || location.name,
      label: location.display_name || location.name || location.code,
    })),
  ], [locations]);

  const selectedLocation = locationOptions.find(
    option => option.value === draftFilters.location
  ) || locationOptions[0];

  return (
    <div className="page-content">
      <div className="w-full px-3 mx-auto xl:max-w-[85%] mb-6">
        <Breadcrumbs title="Stock Levels" breadcrumbItem="Stock Allocation" />

        <Card className="overflow-hidden">
          <CardHeader className="p-5 pb-0">
            <CardTitle className="text-base leading-6 text-slate-700">
              Stock Allocation
            </CardTitle>
            <CardDescription className="text-sm text-slate-500">
              See which orders hold reserved stock and which order lines are still unallocated.
            </CardDescription>
          </CardHeader>

          <CardContent className="p-5 pt-5">
            <form
              onSubmit={applyFilters}
              className="mb-2 flex flex-wrap items-end gap-3"
            >
              <div className="w-[220px] flex-none">
                <label
                  className="mb-2 block text-[13px] font-medium text-slate-900"
                  htmlFor="stock-allocation-location"
                >
                  Location
                </label>
                <Select
                  inputId="stock-allocation-location"
                  className="text-sm"
                  value={selectedLocation}
                  options={locationOptions}
                  styles={LOCATION_SELECT_STYLES}
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                  onChange={option => setDraftFilters(current => ({
                    ...current,
                    location: option?.value || 'all',
                  }))}
                />
              </div>

              <div className="w-[220px] flex-none">
                <label
                  className="mb-2 block text-[13px] font-medium text-slate-900"
                  htmlFor="stock-allocation-order"
                >
                  Order
                </label>
                <Input
                  id="stock-allocation-order"
                  className="h-[38px] border-[#ced4da]"
                  value={draftFilters.order}
                  onChange={event => setDraftFilters(current => ({
                    ...current,
                    order: event.target.value,
                  }))}
                  placeholder="Search Order"
                />
              </div>

              <div className="w-[220px] flex-none">
                <label
                  className="mb-2 block text-[13px] font-medium text-slate-900"
                  htmlFor="stock-allocation-sku"
                >
                  SKU
                </label>
                <Input
                  id="stock-allocation-sku"
                  className="h-[38px] border-[#ced4da]"
                  value={draftFilters.sku}
                  onChange={event => setDraftFilters(current => ({
                    ...current,
                    sku: event.target.value,
                  }))}
                  placeholder="Search SKU"
                />
              </div>

              <div className="w-[180px] flex-none">
                <label
                  className="mb-2 block text-[13px] font-medium text-slate-900"
                  htmlFor="stock-allocation-from-date"
                >
                  From Date
                </label>
                <Input
                  id="stock-allocation-from-date"
                  type="date"
                  className="h-[38px] border-[#ced4da]"
                  value={draftFilters.fromDate}
                  max={draftFilters.toDate || undefined}
                  onChange={event => setDraftFilters(current => ({
                    ...current,
                    fromDate: event.target.value,
                  }))}
                />
              </div>

              <div className="w-[180px] flex-none">
                <label
                  className="mb-2 block text-[13px] font-medium text-slate-900"
                  htmlFor="stock-allocation-to-date"
                >
                  To Date
                </label>
                <Input
                  id="stock-allocation-to-date"
                  type="date"
                  className="h-[38px] border-[#ced4da]"
                  value={draftFilters.toDate}
                  min={draftFilters.fromDate || undefined}
                  onChange={event => setDraftFilters(current => ({
                    ...current,
                    toDate: event.target.value,
                  }))}
                />
              </div>

              <div className="flex-none">
                <Button type="submit" className="h-[38px] min-w-[110px]">
                  <Search className="h-4 w-4 mr-2" />
                  Search
                </Button>
              </div>
            </form>

            <div className="mb-5 text-[13px] text-slate-600">
              <span className="font-semibold">Reserved:</span>{' '}
              <span className="tabular-nums">{summary.reserved}</span>
              <span className="mx-2 text-slate-300">·</span>
              <span className="font-semibold">Unallocated:</span>{' '}
              <span className="tabular-nums">{summary.unallocated}</span>
            </div>

            <div className="overflow-hidden rounded-md border">
              <Table className="min-w-[980px] text-[13px] text-slate-600 [&_td]:whitespace-nowrap [&_td]:px-4 [&_td]:py-3 [&_th]:whitespace-nowrap">
                <TableHeader className="bg-slate-50">
                  <TableRow>
                    <TableHead className="text-left font-semibold text-slate-500">Order</TableHead>
                    <TableHead className="text-left font-semibold text-slate-500">SKU</TableHead>
                    <TableHead className="text-left font-semibold text-slate-500">Location</TableHead>
                    <TableHead className="text-right font-semibold text-slate-500">Reserved</TableHead>
                    <TableHead className="text-right font-semibold text-slate-500">Unallocated</TableHead>
                    <TableHead className="text-left font-semibold text-slate-500">Status</TableHead>
                  </TableRow>
                </TableHeader>

                {state === 'loading' ? (
                  <TableSkeleton columns={6} perPage={Math.min(perPage, 10)} />
                ) : (
                  <TableBody>
                    {state === 'error' ? (
                      <TableRow>
                        <TableCell colSpan={6} className="py-10 text-center text-slate-500">
                          {errorMessage}
                        </TableCell>
                      </TableRow>
                    ) : rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="py-10 text-center text-slate-500">
                          No stock allocation matches these filters.
                        </TableCell>
                      </TableRow>
                    ) : groupedRows.map(row => (
                      <TableRow key={row.id}>
                        {row.orderGroupIndex === 0 && (
                          <TableCell rowSpan={row.orderGroupSize}>
                            {row.orderPath ? (
                              <a
                                href={row.orderPath}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center font-semibold text-slate-700 hover:underline"
                                title="View order"
                              >
                                {row.orderName}
                              </a>
                            ) : (
                              <span className="font-semibold text-slate-700">{row.orderName}</span>
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          <Badge className="font-size-12 badge-soft-primary">
                            {row.sku}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <LocationBadge
                            location={locationNamesByCode.get(row.locationCode)
                              || row.locationName
                              || row.locationCode}
                          />
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.reservedQuantity ?? 0}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.unallocatedQuantity ?? 0}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={row.status} type="fulfillment" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                )}
              </Table>
            </div>

            {state === 'success' && count > 0 && (
              <TablePagination
                count={count}
                className="mt-4"
                page={page}
                setPage={setPage}
                perPage={perPage}
                setPerPage={handlePerPageChange}
                perPageOptions={PER_PAGE_OPTIONS}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StockAllocationPage;
